import CircularProgress from '@mui/material/CircularProgress';
import { darken, lighten, rgbToHex, styled } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type Location, useLocation, useNavigate } from 'react-router-dom';
import Websocket from 'react-websocket';
import { setLastLeadListUpdate, setUnchangedData } from './_flux/Actions';
import { type FluxStore } from './_flux/Store';
import Nav from './_navi/Nav';
import { getNotificationsOffen } from './_tools/AxiosSupport';
import { GBASE_DOMAIN } from './_tools/AxiosVars';
import DialogBox from './_tools/DialogBox';
import Meta from './_tools/Meta';
import Snackbar from './_tools/Snackbar';
import Toast from './_tools/Toast';
import { type BetterNavigateOptions, betterUseNavigate, checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull, parseJson } from './_tools/Tools';
import EnsureLoggedInContainer from './login/EnsureLoggedInContainer';
import { type CustomTheme } from './provider/ThemeProvider';
import { LEAD } from './vars';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    '&.notistack-MuiContent': {
        display: 'flex',
        flexWrap: 'nowrap',
    },
    '&.notistack-MuiContent-error': {
        background: 'linear-gradient(60deg, ' + rgbToHex(lighten('#f44336', 0.1)) + ', ' + rgbToHex(darken('#f44336', 0.1)) + ')',
    },
    '&.notistack-MuiContent-warning': {
        background: 'linear-gradient(60deg, ' + rgbToHex(lighten('#ff9800', 0.1)) + ', ' + rgbToHex(darken('#ff9800', 0.1)) + ')',
    },
    '&.notistack-MuiContent-success': {
        background: 'linear-gradient(60deg, ' + rgbToHex(lighten('#4caf50', 0.1)) + ', ' + rgbToHex(darken('#4caf50', 0.1)) + ')',
    },
    '&.notistack-MuiContent-info': {
        background: 'linear-gradient(60deg, ' + rgbToHex(lighten('#00acc1', 0.1)) + ', ' + rgbToHex(darken('#00acc1', 0.1)) + ')',
    },
}));

const unchangedDataListener = (e) => {
    e.preventDefault();
    e.returnValue = '';
    return '';
};

const App = () => {
    const loginToken = useSelector((state: FluxStore) => state.loginToken);
    const unchangedData = useSelector((state: FluxStore) => state.unchangedData);
    const dispatch = useDispatch();

    const [globalMessages, setGlobalMessages] = useState<any>([]);
    const [hiddenMessages, setHiddenMessages] = useState<any>([]);
    const [showWarning, setShowWarning] = useState<BetterNavigateOptions | undefined>(undefined);
    const loc: Location & { state: { [index: string]: string | number | boolean } } = useLocation();
    const navigate = useNavigate();
    const theme: CustomTheme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const sm = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    const heightToolbar = location.href.includes('/login') || location.href.includes('/loginr') ? 0 : xs ? 48 : sm ? 56 : 64;
    const widthSidebar = location.href.includes('/login') || location.href.includes('/loginr') || xs ? 0 : sm ? 48 : 56;

    const handleMessage = (res) => {
        const data = parseJson(res);
        if (checkUndefined(data)) return;
        if (data.t === 1) {
            // msg
            dispatch(setLastLeadListUpdate(0));
            Toast.showNotification(data.o.f, data.o.m);
            Toast.addMsgWS(data.o.f, data.o.m, App.getLevelString(data.o));
        } else if (data.t === 3) {
            // Neuer Lead eingetroffen
            dispatch(setLastLeadListUpdate(0));
            Toast.showNotification(data.o.f, data.o.m, undefined, 'sysMsgWs');
            Toast.addSysMsgWS(data.o.f, data.o.m, App.getLevelString(data.o));
        } else if (data.t === 4) {
            // caller info
            dispatch(setLastLeadListUpdate(0));
            Toast.showNotification(data.o.f, data.o.m, undefined, 'callerMsgWs', { id: data.o.i });
            Toast.addCallerMsgWS(data.o.f, data.o.m, App.getLevelString(data.o), data.o.i);
        } else if (data.t === 5) {
            // Antwort kunde
            dispatch(setLastLeadListUpdate(0));
            Toast.showNotification(data.o.f, data.o.m, undefined, 'msgWsAntwortKunde', { id: data.o.i });
            Toast.addMsgWSAntwortKunde(data.o.f, data.o.m, App.getLevelString(data.o), data.o.i);
        } else if (data.t === 6) {
            // Fehler beim senden
            dispatch(setLastLeadListUpdate(0));
            Toast.showNotification(data.o.f, data.o.m, undefined, 'msgWsFehlerSenden', { id: data.o.i });
            Toast.addMsgWSFehlerSenden(data.o.f, data.o.m, App.getLevelString(data.o), data.o.i);
        } else if (data.t === 7) {
            // Leads wurden verändert
            dispatch(setLastLeadListUpdate(0));
        }
    };

    const clearMessage = (id) => {
        const array = Object.assign([], hiddenMessages);
        array.push(id);
        setHiddenMessages(array);
    };

    const getNotifications = async () => {
        await getNotificationsOffen().then(({ data: d }) => setGlobalMessages(d));
    };

    useEffect(
        function () {
            getNotifications();
        },
        [loc.pathname]
    );

    useEffect(
        function () {
            if (unchangedData) window.addEventListener('beforeunload', unchangedDataListener, true);
            else window.removeEventListener('beforeunload', unchangedDataListener, true);
        },
        [unchangedData]
    );

    useEffect(
        function () {
            if (!checkUndefinedNullOrEmpty(loc?.state?.unchangedNavigateOptions)) setShowWarning(loc?.state?.unchangedNavigateOptions);
        },
        [loc?.state]
    );

    document.body.style.background = theme?.palette?.background.default;
    const webSocketUrl = !checkUndefinedNullOrEmpty(loginToken) ? (LEAD ? 'wss://' + GBASE_DOMAIN + '/ws/' : 'wss://81.201.150.220:8082/ws/') + loginToken : undefined;

    return (
        <SnackbarProvider
            key="snackbarProvider"
            maxSnack={10}
            preventDuplicate
            iconVariant={{
                default: <CircularProgress size={20} className="iconMarginRight" color="primary" />,
            }}
            Components={{
                default: StyledMaterialDesignContent,
                success: StyledMaterialDesignContent,
                warning: StyledMaterialDesignContent,
                info: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
            }}
        >
            <Meta key="meta" />
            {!checkUndefinedOrNull(showWarning) && (
                <DialogBox
                    key="unchangedDataDialog"
                    dialogData={{
                        warning: true,
                        title: 'Ungespeicherte Änderungen verwerfen?',
                        message: 'Es sind noch ungespeicherte Änderungen vorhanden.\n\nFortfahren?',
                        positiveAnswer: () => {
                            dispatch(setUnchangedData(false));
                            setShowWarning(undefined);
                            betterUseNavigate(navigate, undefined, showWarning, false);
                        },
                        negativeAnswer: () => setShowWarning(undefined),
                    }}
                />
            )}
            {!checkUndefinedNullOrEmpty(webSocketUrl) ? <Websocket key="websocker" url={webSocketUrl} onMessage={handleMessage} reconnect /> : undefined}
            {!checkUndefinedNullOrEmpty(loginToken) && !location.href.includes('/login') && !location.href.includes('/loginr') && <Nav key="navigation" />}
            <main
                key="mainContent"
                id="mainContainer"
                style={{
                    width: 'calc(100vw - ' + widthSidebar + 'px)',
                    height: 'calc(100vh - ' + heightToolbar + 'px)',
                    marginLeft: widthSidebar,
                    marginTop: heightToolbar,
                    padding: theme.spacing(2),
                    overflow: 'auto',
                }}
            >
                <Toast key="toast" />
                {!checkUndefinedNullOrEmpty(loginToken) &&
                    !location.href.includes('/login') &&
                    !location.href.includes('/loginr') &&
                    globalMessages.map((message) => {
                        if (!hiddenMessages.includes(message.id)) {
                            return (
                                <Snackbar
                                    abort={message.noHide ? undefined : () => clearMessage(message.id)}
                                    key={'snackBar' + message.id}
                                    error={message.level === 1}
                                    warning={message.level === 2}
                                    success={message.level === 3}
                                    info={message.level === 4}
                                    message={message.m}
                                />
                            );
                        }
                    })}
                <EnsureLoggedInContainer />
            </main>
        </SnackbarProvider>
    );
};

export default App;

App.getLevelString = (o) => {
    if (2 === o.l) return 'warning';
    if (3 === o.l) return 'error';
    return 'info';
};
